// src/pages/TendersPage.js
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
  TENDER_STATUSES, TENDER_SECTORS, PRIORITIES,
  formatCurrency, formatDate, getLabelFor, daysUntilColor
} from '../utils/constants';
import TenderFormModal from '../components/TenderFormModal';

const RAW_STATUSES = [
  { value: '',          label: 'All',       color: 'var(--text-secondary)' },
  { value: 'OPEN',      label: 'Open',      color: 'var(--green)' },
  { value: 'CLOSED',    label: 'Closed',    color: 'var(--text-muted)' },
  { value: 'AWARDED',   label: 'Awarded',   color: 'var(--blue)' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'var(--red)' },
  { value: 'UNKNOWN',   label: 'Unknown',   color: 'var(--text-muted)' },
];

const CATEGORIES = [
  { value: 'MINING',       label: 'Mining & Underground', color: '#e8a020' },
  { value: 'PROJECT_MGMT', label: 'Project Management',   color: '#388bfd' },
  { value: 'INDIGENOUS',   label: 'Indigenous',           color: '#bc8cff' },
  { value: 'OIL_GAS',      label: 'Oil & Gas',            color: '#f85149' },
  { value: 'CIVIL',        label: 'Civil Construction',   color: '#3fb950' },
  { value: 'MECHANICAL',   label: 'Mechanical',           color: '#8b949e' },
  { value: 'REMEDIATION',  label: 'Remediation',          color: '#58a6ff' },
];

export default function TendersPage() {
  const navigate = useNavigate();
  const [tenders, setTenders] = useState([]);
  const [summary, setSummary] = useState({});
  const [rawSummary, setRawSummary] = useState({});
  const [sources, setSources] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    tender_status_raw: 'OPEN',
    sector: '', priority: '', search: '',
    source_name: '', province: '', category: '',
  });
  const [sort, setSort] = useState({ field: 'closing_date', dir: 'asc' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set('sort', sort.field);
      params.set('dir', sort.dir);
      const data = await api.get(`/tenders?${params}`);
      setTenders(data.tenders || []);
      setSummary(data.summary || {});
      setRawSummary(data.raw_summary || {});
      setSources(data.sources || []);
      setProvinces(data.provinces || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filters, sort]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters(f => ({ ...f, sector: '', priority: '', search: '', source_name: '', province: '', category: '' }));
  const totalCount = Object.values(rawSummary).reduce((a, b) => a + b, 0);
  const hasFilters = filters.sector || filters.priority || filters.search || filters.source_name || filters.province || filters.category;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Tenders</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>{totalCount} total tracked</div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-secondary" onClick={() => setShowFilters(f => !f)}>
            <FilterIcon /> {showFilters ? 'Hide' : 'Filters'} {hasFilters ? '●' : ''}
          </button>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            <PlusIcon /> New Tender
          </button>
        </div>
      </div>

      {/* Raw status tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: '1rem', borderBottom: '1px solid var(--border)' }}>
        {RAW_STATUSES.map(s => {
          const count = s.value === '' ? totalCount : (rawSummary[s.value] || 0);
          const isActive = filters.tender_status_raw === s.value;
          return (
            <button key={s.value} onClick={() => setFilter('tender_status_raw', s.value)} style={{
              padding: '0.6rem 1rem', border: 'none',
              borderBottom: isActive ? `2px solid ${s.color}` : '2px solid transparent',
              background: 'transparent',
              color: isActive ? s.color : 'var(--text-muted)',
              fontWeight: isActive ? 700 : 400, fontSize: '0.85rem',
              cursor: 'pointer', transition: 'all 0.15s',
              marginBottom: '-1px', whiteSpace: 'nowrap',
            }}>
              {s.label}
              {count > 0 && (
                <span style={{
                  marginLeft: '0.4rem',
                  background: isActive ? s.color : 'var(--bg-hover)',
                  color: isActive ? '#000' : 'var(--text-muted)',
                  borderRadius: 10, padding: '0.05rem 0.4rem',
                  fontSize: '0.7rem', fontWeight: 700,
                }}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Category quick filters */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {CATEGORIES.map(cat => {
          const isActive = filters.category === cat.value;
          return (
            <button key={cat.value} onClick={() => setFilter('category', isActive ? '' : cat.value)} style={{
              padding: '0.25rem 0.75rem', borderRadius: 20,
              border: `1px solid ${isActive ? cat.color : 'var(--border)'}`,
              background: isActive ? cat.color + '22' : 'transparent',
              color: isActive ? cat.color : 'var(--text-muted)',
              fontSize: '0.78rem', fontWeight: isActive ? 700 : 400,
              cursor: 'pointer', transition: 'all 0.1s',
            }}>
              {cat.label}
            </button>
          );
        })}
        {filters.category && (
          <button className="btn btn-ghost" style={{ fontSize: '0.78rem', padding: '0.25rem 0.5rem' }}
            onClick={() => setFilter('category', '')}>✕ Clear</button>
        )}
      </div>

      {/* Expanded filters panel */}
      {showFilters && (
        <div className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div className="form-label" style={{ marginBottom: '0.3rem' }}>Search</div>
              <input type="text" placeholder="Title, client, description…"
                value={filters.search} onChange={e => setFilter('search', e.target.value)} />
            </div>
            <div style={{ minWidth: 150 }}>
              <div className="form-label" style={{ marginBottom: '0.3rem' }}>Source</div>
              <select value={filters.source_name} onChange={e => setFilter('source_name', e.target.value)}>
                <option value="">All Sources</option>
                {sources.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ minWidth: 160 }}>
              <div className="form-label" style={{ marginBottom: '0.3rem' }}>Province</div>
              <select value={filters.province} onChange={e => setFilter('province', e.target.value)}>
                <option value="">All Provinces</option>
                {provinces.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div style={{ minWidth: 180 }}>
              <div className="form-label" style={{ marginBottom: '0.3rem' }}>Sector</div>
              <select value={filters.sector} onChange={e => setFilter('sector', e.target.value)}>
                <option value="">All Sectors</option>
                {TENDER_SECTORS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div style={{ minWidth: 160 }}>
              <div className="form-label" style={{ marginBottom: '0.3rem' }}>Sort By</div>
              <select value={sort.field + '_' + sort.dir}
                onChange={e => { const [f, d] = e.target.value.split('_'); setSort({ field: f, dir: d }); }}>
                <option value="closing_date_asc">Closing Date ↑</option>
                <option value="closing_date_desc">Closing Date ↓</option>
                <option value="published_date_desc">Published ↓</option>
                <option value="created_at_desc">Date Added ↓</option>
              </select>
            </div>
            {hasFilters && (
              <button className="btn btn-ghost" onClick={clearFilters}>Clear all</button>
            )}
          </div>
        </div>
      )}

      {/* Results count */}
      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
        {loading ? 'Loading…' : `${tenders.length} tender${tenders.length !== 1 ? 's' : ''} shown`}
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
        ) : tenders.length === 0 ? (
          <div className="empty-state">
            <DocIcon />
            <h3>No tenders found</h3>
            <p style={{ fontSize: '0.85rem' }}>Try adjusting your filters.</p>
            <button className="btn btn-primary" onClick={() => setShowForm(true)}>Add Tender</button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tender</th>
                  <th>Category</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Province</th>
                  <th>Closing</th>
                  <th>Days Left</th>
                </tr>
              </thead>
              <tbody>
                {tenders.map(t => {
                  const days = t.days_until_close !== null ? parseInt(t.days_until_close) : null;
                  const rawStatus = t.tender_status_raw || '';
                  const statusColor = rawStatus === 'OPEN' ? 'var(--green)'
                    : rawStatus === 'AWARDED' ? 'var(--blue)'
                    : rawStatus === 'CANCELLED' ? 'var(--red)' : 'var(--text-muted)';

                  // Parse categories
                  const cats = t.categories ? t.categories.split(',').filter(Boolean) : [];

                  return (
                    <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/tenders/${t.id}`)}>
                      <td style={{ maxWidth: 340 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.3 }}>{t.title}</div>
                        {(t.client_name_db || t.client_name) && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                            {t.client_name_db || t.client_name}
                          </div>
                        )}
                        {t.reference_number && (
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace', marginTop: 1 }}>
                            {t.reference_number}
                          </div>
                        )}
                        {t.description && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.4 }}>
                            {t.description.slice(0, 100)}…
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {cats.slice(0, 2).map(cat => {
                            const catDef = CATEGORIES.find(c => c.value === cat);
                            return catDef ? (
                              <span key={cat} style={{
                                fontSize: '0.68rem', fontWeight: 700,
                                color: catDef.color, background: catDef.color + '22',
                                padding: '0.1rem 0.45rem', borderRadius: 8, whiteSpace: 'nowrap',
                              }}>{catDef.label}</span>
                            ) : null;
                          })}
                          {!cats.length && t.tender_type && (
                            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{t.tender_type}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {t.source_name && (
                          <span style={{
                            fontSize: '0.72rem', background: 'var(--bg-hover)',
                            color: 'var(--text-secondary)', padding: '0.15rem 0.5rem',
                            borderRadius: 10, whiteSpace: 'nowrap',
                          }}>{t.source_name}</span>
                        )}
                      </td>
                      <td>
                        <span style={{
                          fontSize: '0.72rem', fontWeight: 700, color: statusColor,
                          background: statusColor + '22',
                          padding: '0.15rem 0.5rem', borderRadius: 10, whiteSpace: 'nowrap',
                        }}>
                          {rawStatus || getLabelFor(TENDER_STATUSES, t.status)}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {t.province || '—'}
                      </td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {formatDate(t.closing_date)}
                      </td>
                      <td>
                        {days !== null ? (
                          <span style={{ fontSize: '0.8rem', fontWeight: 700, color: daysUntilColor(days) }}>
                            {days < 0 ? `${Math.abs(days)}d ago` : days === 0 ? 'Today' : `${days}d`}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && <TenderFormModal onClose={() => setShowForm(false)} onSaved={t => { setShowForm(false); navigate(`/tenders/${t.id}`); }} />}
    </div>
  );
}

function PlusIcon()   { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function FilterIcon() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>; }
function DocIcon()    { return <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>; }
